import type { ISampleMetaData, PureMetadata } from './ISampleMetaData.js'

/**
 * Which metadata fields go when a caller clears one, keyed by the name the CSV selectors and the sample
 * table both use for the field.
 *
 * An enum owns two fields — itself and the free-text twin that an unmapped CSV value lands in via
 * `AS_CUSTOM` — and clearing has to take both, or stale custom text survives under a cleared enum.
 *
 * Shared so the importer and the table cannot disagree about what clearing a field means.
 */
export const CLEARS_FIELDS: Record<string, readonly (keyof PureMetadata)[]> = {
  samplingDate: ['samplingDate'],
  samplingStart: ['samplingStart'],
  samplingEnd: ['samplingEnd'],
  sequencingDepth: ['sequencingDepth'],
  sampleCoordinates: ['sampleCoordinates'],
  matrixType: ['matrixType', 'matrixTypeCustom'],
  extractionKit: ['extractionKit', 'extractionKitCustom'],
  trapType: ['trapType', 'trapTypeCustom'],
  collectionType: ['collectionType', 'collectionTypeCustom'],
  sequencer: ['sequencer', 'sequencerCustom'],
  sequencingChemistry: ['sequencingChemistry', 'sequencingChemistryCustom'],
  localityType: ['localityType', 'localityTypeCustom'],
  habitatType: ['habitatType', 'habitatTypeCustom']
}

/**
 * Empty for a name that owns no metadata field — the CSV's primer, sequence-id and split coordinate
 * columns among them, which is why a clear sentinel in those columns does nothing.
 */
export const fieldsClearedBy = (field: string | undefined): readonly (keyof PureMetadata)[] =>
  (field && CLEARS_FIELDS[field]) || []

/**
 * A copy of [metadata] with [fields] removed rather than set to undefined: the update endpoint replaces
 * the whole row, and an absent key is what it reads as null. Setting undefined would serialise to the
 * same thing today, but only by accident of the serialiser dropping it.
 */
export const withFieldsCleared = (
  metadata: ISampleMetaData,
  fields: readonly (keyof PureMetadata)[]
): ISampleMetaData => {
  const next = { ...metadata }
  fields.forEach((field) => delete next[field])
  return next
}
